// Legal / policy page content. Rendered by components/PolicyPage.js.
// Block types: p (paragraph), list (bullets), steps (numbered), faq ({ q, a }), note (callout).
import { SITE } from './siteContent';

const BRAND = 'VidyarthiKart';
const EMAIL = SITE.supportEmail;

export const POLICY_LAST_UPDATED = '7 October 2026';

export const POLICIES = [
  {
    slug: 'privacy-policy',
    path: '/privacy-policy',
    title: 'Privacy Policy',
    icon: 'shield',
    summary: 'What information we collect, how we use it and how we keep it safe.',
    intro: [
      `At ${BRAND}, we prioritize your privacy. When you visit our website or app, you may provide certain details necessary for completing transactions or activities. We take all reasonable measures to ensure the protection of your information and use it responsibly. The data we collect is retained and used to enhance your experience, ensuring a more user-friendly environment and improving your book browsing and online purchasing experience over time.`,
      'We are committed to keeping you informed about the data we collect and how it helps us serve you better. Our policy is regularly updated to align with industry trends, user preferences, technology advancements, and other relevant factors. We encourage you to review these updates periodically.',
      `The following Privacy Policy applies to all information shared by users on ${BRAND}. We guarantee that your information will be safe with us according to the terms outlined in this policy.`,
    ],
    sections: [
      {
        heading: 'Information Collection Policy',
        blocks: [
          { type: 'p', text: 'To use the website, users must register by providing personal details such as name, address, email, and other information as required by the registration form.' },
          { type: 'p', text: 'This information is stored in cookies and log files to generate a user profile, and each user is assigned a unique identification number. This helps us track user preferences and improve our services to better meet your needs.' },
          { type: 'p', text: 'Our website automatically tracks certain usage data, which may include, but is not limited to, URL tracking, browser type, IP address, pages viewed, ads clicked, and other actions taken while using the website. Cookies are used to track usage, and third-party advertisers may also place cookies on your browser. Please note that we are not responsible for these external advertisements.' },
          { type: 'p', text: 'We may also collect information about your book or content preferences. Additionally, we may store any correspondence you send us, such as emails, as well as responses to online polls, surveys, ads, or questionnaires.' },
          { type: 'p', text: 'For certain sections of the website, you may be required to provide additional personal information, such as payment details, depending on the specific services you wish to access.' },
          { type: 'p', text: 'The website will use the collected data to provide the services requested by users, and profile/general information will be used to track viewing preferences.' },
        ],
      },
      {
        heading: 'Cookies',
        blocks: [
          { type: 'p', text: 'A cookie is a small text file stored on your computer by your web browser. Cookies can be used for a variety of purposes, including authentication, session tracking, saving preferences, and maintaining shopping cart contents. We may use cookies to track user preferences and personalize services.' },
          { type: 'p', text: 'Third-party advertisements on our website may set cookies on your computer, and we are not responsible for these cookies.' },
        ],
      },
      {
        heading: 'Subscription',
        blocks: [
          { type: 'p', text: 'If you make a purchase, subscribe to any module, or register for services on the website, you may receive emails or messages regarding those services. These communications may include information such as OTP or password generation, or updates to your profile.' },
        ],
      },
      {
        heading: 'Notices',
        blocks: [
          { type: 'p', text: 'From time to time, we may send users service-related notices, such as maintenance updates, upgrades, or temporary suspensions of services, via email or SMS to the registered account.' },
        ],
      },
      {
        heading: 'Promotional Content',
        blocks: [{ type: 'p', text: 'Communications related to our services may be considered promotional in nature.' }],
      },
      {
        heading: 'Sharing of Information',
        blocks: [
          { type: 'p', text: 'Your registered profile will not be shared with third parties without your prior written consent. Your information will remain confidential unless you choose to make it public or if disclosure is required by law, such as in response to law enforcement authorities or as part of a criminal investigation or legal proceedings.' },
        ],
      },
      {
        heading: 'Verification',
        blocks: [{ type: 'p', text: 'We may use the information provided by you to verify your credentials before establishing a relationship.' }],
      },
      {
        heading: 'Updating Information',
        blocks: [{ type: 'p', text: 'You have the option to update any of your personal information at any time from your profile.' }],
      },
      {
        heading: 'Protection of User Information',
        blocks: [
          { type: 'p', text: 'User information is restricted to authorized personnel who require access to provide services. All users accessing this data must do so through a password-protected account, and any breach of confidentiality by our employees will result in strict penalties. Our servers, which store your data, are maintained in a secure environment.' },
          { type: 'p', text: 'However, we may share aggregated, non-personally identifiable information with partners for research and analysis purposes.' },
        ],
      },
      {
        heading: 'Ownership of Information',
        blocks: [
          { type: 'p', text: `${BRAND} reserves the right to sell, share, or transfer personally identifiable information to any successor in interest, such as in the event of a sale of the company to a third party. ${BRAND} will retain ownership of the information collected.` },
        ],
      },
    ],
  },
  {
    slug: 'terms-and-conditions',
    path: '/terms-and-conditions',
    title: 'Terms & Conditions',
    icon: 'book',
    summary: 'The rules that apply when you browse and shop with us.',
    intro: [
      `By accessing and using ${BRAND}, you agree to comply with the terms and conditions set forth in this document. These terms apply to all users of the website and app, including but not limited to users who are browsers, customers, merchants, and contributors of content.`,
    ],
    sections: [
      {
        heading: 'Information Collection and Usage',
        blocks: [
          { type: 'p', text: 'To use the website, users must register and provide personal details such as name, address, email address, and other required information. The website stores information through cookies and log files to create a profile for the user, which helps us track preferences and improve website services.' },
          { type: 'p', text: 'The website collects various data automatically, such as your IP address, browser type, URL tracking, and actions taken while using the website. This data is collected through cookies and is used to personalize the user experience and improve service offerings. We may also collect personal correspondence, responses to surveys, and other interactions you have with the website.' },
        ],
      },
      {
        heading: 'Cookies',
        blocks: [
          { type: 'p', text: 'A cookie is a small text file stored on your computer by a web browser. These cookies may be used to track preferences, maintain session states, and store information such as shopping cart contents. Third-party advertisers may also place cookies on your computer while you interact with ads on the website, but we are not responsible for these cookies.' },
        ],
      },
      {
        heading: 'Log Files',
        blocks: [
          { type: 'p', text: 'The website collects log file data, such as your IP address, ISP, and click patterns, which are not linked to personally identifiable information but are used to track user movement on the website and gather broad demographic data.' },
        ],
      },
      {
        heading: 'Subscription and Communications',
        blocks: [
          { type: 'p', text: 'By making a purchase or subscribing to any services on the website, users may receive emails or messages related to their account, including OTPs, profile status, or other requested information. These communications are essential to service users and may include promotional content.' },
        ],
      },
      {
        heading: 'Sharing of Information',
        blocks: [
          { type: 'p', text: `Your registered profile will not be shared with third parties without your prior written consent. However, in certain cases, we may disclose your information to law enforcement authorities or government entities in connection with investigations or legal matters. We will also share information with a successor entity if ${BRAND} is sold.` },
        ],
      },
      {
        heading: 'User Information Changes',
        blocks: [
          { type: 'p', text: 'Users can update or change their information at any time by accessing their account settings. You may also deactivate your profile at any time if you no longer wish to use our services.' },
        ],
      },
      {
        heading: 'Protection of User Information',
        blocks: [
          { type: 'p', text: 'We take all reasonable steps to protect your personal information. Access to user information is restricted to authorized personnel only and is protected through password-protected accounts. Any breach of confidentiality by our employees will be dealt with strictly. However, we cannot guarantee the security of information transmitted over the internet.' },
        ],
      },
      {
        heading: 'Privacy and Confidentiality',
        blocks: [
          { type: 'p', text: 'All user information collected will remain private. In certain circumstances, we may share aggregated statistical data for research purposes with our partners or associates. However, your personally identifiable information will not be shared without your consent, unless required by law or for business transfer purposes.' },
        ],
      },
      {
        heading: 'Changes to the Privacy Policy and Terms & Conditions',
        blocks: [
          { type: 'p', text: 'We may update our privacy policy and terms and conditions regularly in response to changes in technology, industry trends, and user preferences. We encourage you to review these documents periodically to stay informed of any updates.' },
        ],
      },
      {
        heading: 'Limitation of Liability',
        blocks: [
          { type: 'p', text: 'While we strive to ensure the accuracy and security of the information on the website, we make no guarantees regarding the availability or functionality of the site, nor do we accept responsibility for any errors, omissions, or interruptions in service.' },
        ],
      },
      {
        heading: 'Governing Law',
        blocks: [
          { type: 'p', text: 'These terms and conditions are governed by and construed in accordance with the laws of India. Any disputes arising out of or relating to these terms shall be resolved under the jurisdiction of the courts in India.' },
        ],
      },
      {
        heading: 'Contact Information',
        blocks: [
          { type: 'p', text: `For any questions or concerns regarding these terms and conditions, Privacy Policy, or any other inquiries, please contact us at ${EMAIL}.` },
        ],
      },
    ],
  },
  {
    slug: 'refund-and-cancellation-policy',
    path: '/refund-and-cancellation-policy',
    title: 'Refund and Cancellation Policy',
    icon: 'refresh',
    summary: 'Replacements, refunds and order cancellations.',
    intro: [
      `At ${BRAND}, we want our customers to be completely satisfied with their purchases. If you're not happy with something, you can request a replacement within 7 days from the date of delivery.`,
      'We take great care in packing and shipping your items to ensure a smooth shopping experience. However, if there is an issue with the product received, we offer an easy return and/or replacement process.',
    ],
    sections: [
      {
        heading: 'Replacement/Exchange Policy',
        blocks: [
          { type: 'p', text: 'We ensure that the items we deliver are in perfect condition. However, there are situations where:' },
          { type: 'list', items: ['The item may get damaged during transit.', 'The item might have a manufacturing defect.', 'You might receive the wrong item.'] },
          { type: 'p', text: 'In such cases, we will replace the item at no additional cost, provided the request for a replacement or return is made within 7 days from the delivery date.' },
        ],
      },
      {
        heading: 'Replacement/Exchange Conditions',
        blocks: [
          { type: 'list', items: ['Items should be in their original condition.', 'Items should ideally be in the original packaging.', 'Return requests must be made within 7 days of delivery.'] },
        ],
      },
      {
        heading: 'How to Request a Return (Books/Stationery/Others)',
        blocks: [
          {
            type: 'steps',
            items: [
              'Log in to your account using your registered mobile number.',
              `Send an email to ${EMAIL} with your order ID and payment ID to request a return.`,
              'Our team will review your request and get back to you within 7-10 business days.',
            ],
          },
          { type: 'note', text: 'Please note: We will not be responsible for returns sent without prior confirmation.' },
        ],
      },
      {
        heading: 'Exchange/Replacement FAQ',
        blocks: [
          { type: 'faq', q: 'What happens if there is a price difference in the replacement item?', a: 'If the replacement item costs more than the original, the customer will need to pay the difference.' },
        ],
      },
      {
        heading: 'Refund Policy',
        blocks: [
          { type: 'p', text: 'Refunds will be processed to the payment method used for the original order. Typically, refunds are completed within 7 days of order cancellation. If there is a delay, you can contact our support team for assistance.' },
        ],
      },
      {
        heading: 'Refund Conditions',
        blocks: [
          {
            type: 'list',
            items: [
              'If the replacement item is unavailable, we will offer alternatives, but the final decision will be yours.',
              '100% refund will be given if the product is unavailable or if we cannot ship the item.',
              'Refunds will be processed within 7-10 days after the returned products are received, either via Bank Transfer or Online Wallet Payments.',
            ],
          },
        ],
      },
      {
        heading: 'How to Request a Refund',
        blocks: [
          {
            type: 'steps',
            items: [
              'Log in to your account using your registered mobile number.',
              `Send an email to ${EMAIL} with your order ID and payment ID to request a refund.`,
              'Our team will review your request and get back to you within 7-10 business days.',
            ],
          },
        ],
      },
      {
        heading: 'Cancellation Policy',
        blocks: [
          { type: 'faq', q: 'Can I cancel my order after payment?', a: 'Yes, you can cancel your order anytime before it is shipped. If the order has already been shipped, the request will be processed under the Replacement/Exchange or Refund policies.' },
        ],
      },
      {
        heading: 'How to Request an Order Cancellation',
        blocks: [
          {
            type: 'steps',
            items: [
              'Log in to your account using your registered mobile number.',
              `Send an email to ${EMAIL} with your order ID and payment ID to request a cancellation.`,
              'Our team will review your request and get back to you within 7-10 business days.',
            ],
          },
        ],
      },
    ],
  },
  {
    slug: 'shipping-and-fulfillment',
    path: '/shipping-and-fulfillment',
    title: 'Shipping & Fulfillment',
    icon: 'truck',
    summary: 'Where we deliver, dispatch timelines and courier partners.',
    intro: [
      'We ship to nearly all locations across India that are served by the Indian Postal Network. When you place an order, we will provide an estimated shipping/dispatch and delivery time based on item availability, your delivery location, and coverage by our partnered couriers.',
    ],
    sections: [
      {
        heading: 'Dispatch Timelines',
        blocks: [
          { type: 'p', text: 'Generally, the dispatch timelines are as follows:' },
          { type: 'faq', q: 'In Stock', a: 'Items in stock will clearly indicate the estimated shipping time. Please allow 24-48 hours for order processing, as we do not ship on weekends or holidays. Expect delivery within 5-7 business days in areas with standard courier services.' },
          { type: 'faq', q: 'Out of Stock', a: 'If an item is out of stock, we will accept email notifications and inform you as soon as the item is available again.' },
          { type: 'faq', q: 'Pre-Order', a: 'For products expected to arrive soon (usually within 2-3 weeks), we accept email notifications and will let you know once the product is ready for shipment so you can place your order.' },
        ],
      },
      {
        heading: 'Handling Charges',
        blocks: [
          { type: 'p', text: 'Some orders may incur Handling Charges, which are applied if the item requires special packing or has a large volumetric weight.' },
        ],
      },
      {
        heading: 'Delivery Partners',
        blocks: [
          { type: 'p', text: 'For areas where our couriers do not have coverage, we will send the order via registered post through India Post, which may take 1-2 weeks depending on the location.' },
          { type: 'p', text: 'Our couriers include Uber and Rapido.' },
          { type: 'p', text: "For major destinations, national courier services typically deliver within 1-4 days. You can track your parcel's status and location on the courier's website using the provided tracking code. For remote areas with no coverage by our partnered couriers, India Post will handle delivery, which may take longer — sometimes exceeding 2 weeks in rare cases." },
          { type: 'note', text: 'We kindly ask for your patience, and if there is an unusual delay, please let us know, and we will follow up with India Post.' },
        ],
      },
    ],
  },
  {
    slug: 'delete-account',
    path: '/delete-account',
    title: 'Account & Data Deletion',
    icon: 'trash',
    summary: 'How to delete your account, or ask us to delete some of your data.',
    intro: [
      `This page explains how users of the ${BRAND} app and website can request deletion of their ${BRAND} account and the personal data linked to it, or request deletion of some of their data without closing the account.`,
    ],
    sections: [
      {
        heading: 'Delete Your Account',
        blocks: [
          { type: 'steps', items: [
            `Send an email to ${EMAIL} from the email address registered with your ${BRAND} account (or mention your registered mobile number).`,
            'Use the subject line "Account Deletion Request".',
            'We may contact you to confirm the request comes from the account owner.',
            'Your account and associated personal data will be deleted within 30 days of confirmation, and we will email you once it is done.',
          ] },
        ],
      },
      {
        heading: 'Delete Some of Your Data',
        blocks: [
          { type: 'p', text: `You can ask us to delete specific data without closing your account — for example saved addresses, student details or your phone number. Email ${EMAIL} with the subject line "Data Deletion Request" and tell us which data you want removed. We will complete the request within 30 days.` },
        ],
      },
      {
        heading: 'What Is Deleted',
        blocks: [
          { type: 'list', items: [
            'Your profile: name, email address and mobile number.',
            'Saved delivery addresses.',
            'Student, school and grade details added to your account.',
            'Cart contents and login sessions.',
          ] },
        ],
      },
      {
        heading: 'What We Keep',
        blocks: [
          { type: 'p', text: 'Order and payment records (invoice details, order items and transaction references) are kept for up to 8 years where required by Indian tax and accounting laws, and are then deleted. We do not store your card or UPI details — payments are processed by Razorpay.' },
          { type: 'note', text: `Questions? Write to ${EMAIL} or call ${SITE.supportPhone}.` },
        ],
      },
    ],
  },
];

export const getPolicyBySlug = (slug) => POLICIES.find((p) => p.slug === slug);

export const POLICY_PATHS = POLICIES.map((p) => p.path);
